import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CAlert,
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CFormGroup,
    CInput,
    CInputGroup,
    CInputGroupAppend,
    CLabel,
    CRow,
    CSelect,
    CSpinner
} from '@coreui/react';
import { getApi } from '../../apis/backendApis';
import { InfraCampo, InfraGrupo, TemplateOpcao } from '../../models/InfraParametros';

interface Props {
    /** Sem id é cliente novo: os valores ficam locais até criar o pipeline. */
    pipelineId?: string;
    onSalvo?: () => void;
    onCriado?: (id: number) => void;
}

const comOValorAtual = (opcoes: Array<string>, atual: string): Array<string> =>
    atual !== '' && opcoes.indexOf(atual) === -1 ? [atual].concat(opcoes) : opcoes;

const semAcento = (texto: string): string =>
    texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** "Teste de Loja" vira "testedeloja.com.br". */
const dominioDoNome = (nome: string): string => {
    const limpo = semAcento(nome).replace(/[^a-z0-9]/g, '');
    return limpo === '' ? '' : limpo + '.com.br';
};

/** "Teste de Loja" vira "testedeloja". */
const slugDoNome = (nome: string): string =>
    semAcento(nome).replace(/[^a-z0-9]/g, '');

/**
 * Os parâmetros de um cliente. A mesma tela serve para criar e para editar
 * depois: só muda de onde os valores vêm e o que o botão do fim faz.
 */
const Parametros = (props: Props) => {
    const { pipelineId, onSalvo, onCriado } = props;
    const criando = !pipelineId;

    const [campos, setCampos] = useState<Array<InfraCampo>>([]);
    const [grupos, setGrupos] = useState<Array<InfraGrupo>>([]);
    const [farms, setFarms] = useState<Array<string>>([]);
    const [templates, setTemplates] = useState<Array<TemplateOpcao>>([]);
    const [rascunho, setRascunho] = useState<{ [chave: string]: string }>({});
    const [carregando, setCarregando] = useState<boolean>(true);
    const [salvando, setSalvando] = useState<boolean>(false);
    const [erro, setErro] = useState<string | null>(null);
    const [aviso, setAviso] = useState<string | null>(null);

    /** O que a tela sugeriu por último: o que o usuário digitar fica de pé. */
    const sugeridoRef = useRef<{ dominio: string; slug: string }>({ dominio: '', slug: '' });

    const carregar = useCallback(() => {
        setCarregando(true);
        const url = criando
            ? '/infra/pipeline/parametros-novo'
            : '/infra/pipeline/' + pipelineId + '/parametros';

        getApi().get(url, criando ? { params: { farm: 'Farm2' } } : undefined)
            .then(resposta => {
                setCampos(resposta.data.campos || []);
                setGrupos(resposta.data.grupos || []);
                setRascunho({});
                setErro(null);
            })
            .catch(motivo => setErro(motivo?.response?.data?.error || 'Não consegui ler os parâmetros.'))
            .finally(() => setCarregando(false));
    }, [pipelineId, criando]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        getApi().get('/infra/pipeline/opcoes')
            .then(resposta => {
                setFarms(resposta.data.farms || []);
                setTemplates(resposta.data.templates || []);
                setAviso(resposta.data.avisoTemplates || null);
            })
            .catch(() => setAviso('Não consegui carregar as opções de farm e template.'));
    }, []);

    const valorOriginal = useCallback((chave: string): string => {
        const campo = campos.filter(item => item.chave === chave)[0];
        const efetivo = campo ? campo.efetivo : null;
        return efetivo === null || efetivo === undefined ? '' : String(efetivo);
    }, [campos]);

    const valorDe = useCallback((chave: string): string =>
        Object.prototype.hasOwnProperty.call(rascunho, chave)
            ? rascunho[chave]
            : valorOriginal(chave),
    [rascunho, valorOriginal]);

    /** Valor igual ao que veio do servidor sai do rascunho: não é alteração. */
    const escrever = useCallback((valores: { [chave: string]: string }) => {
        setRascunho(atual => {
            const novo = { ...atual };
            Object.keys(valores).forEach(chave => {
                if (valores[chave] === valorOriginal(chave)) {
                    delete novo[chave];
                } else {
                    novo[chave] = valores[chave];
                }
            });
            return novo;
        });
    }, [valorOriginal]);

    const farmAtual = valorDe('farm');

    const templatesDaFarm = useMemo(
        () => templates.filter(item => item.farm === null || item.farm === farmAtual),
        [templates, farmAtual]
    );

    const alterados = Object.keys(rascunho);

    /** Recalcula slug, banco, buckets e hosts a partir do domínio digitado. */
    const preencherPadrao = () => {
        getApi().get('/infra/pipeline/padroes', {
            params: { nome: valorDe('nome'), dominio: valorDe('dominio'), farm: farmAtual }
        })
            .then(resposta => {
                const valores = resposta.data.valores || {};
                const texto: { [chave: string]: string } = {};
                Object.keys(valores).forEach(chave => {
                    texto[chave] = valores[chave] === null ? '' : String(valores[chave]);
                });
                escrever(texto);
            })
            .catch(motivo => setErro(motivo?.response?.data?.error || 'Não consegui montar o padrão.'));
    };

    const valoresParaEnviar = () => {
        const valores: { [chave: string]: string } = {};
        campos.forEach(campo => { valores[campo.chave] = valorDe(campo.chave); });
        return valores;
    };

    const criar = () => {
        setSalvando(true);
        setErro(null);

        getApi().post('/infra/pipeline/create', valoresParaEnviar())
            .then(resposta => onCriado && onCriado(resposta.data.id))
            .catch(motivo => {
                setErro(motivo?.response?.data?.error || 'Não consegui criar o pipeline.');
                setSalvando(false);
            });
    };

    const salvar = () => {
        if (alterados.length === 0) {
            return;
        }
        setSalvando(true);
        setErro(null);

        getApi().post('/infra/pipeline/' + pipelineId + '/parametros', { valores: rascunho })
            .then(resposta => {
                setCampos(resposta.data.campos || []);
                setGrupos(resposta.data.grupos || []);
                setRascunho({});
                if (onSalvo) {
                    onSalvo();
                }
            })
            .catch(motivo => setErro(motivo?.response?.data?.error || 'Não consegui salvar.'))
            .finally(() => setSalvando(false));
    };

    const renderEntrada = (campo: InfraCampo) => {
        const valor = valorDe(campo.chave);
        const comum = {
            id: 'param-' + campo.chave,
            value: valor,
            disabled: !campo.editavel || salvando,
            onChange: (evento: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
                const digitado = evento.target.value;

                if (campo.chave === 'nome') {
                    const novos: { [chave: string]: string } = { nome: digitado };

                    // Cada campo só continua acompanhando o nome enquanto
                    // ninguém tiver escrito um valor próprio nele.
                    const dominio = dominioDoNome(digitado);
                    if (valorDe('dominio') === '' || valorDe('dominio') === sugeridoRef.current.dominio) {
                        novos.dominio = dominio;
                        sugeridoRef.current.dominio = dominio;
                    }

                    const slug = slugDoNome(digitado);
                    if (valorDe('slug') === '' || valorDe('slug') === sugeridoRef.current.slug) {
                        novos.slug = slug;
                        sugeridoRef.current.slug = slug;
                    }

                    escrever(novos);
                    return;
                }

                escrever({ [campo.chave]: digitado });
            }
        };

        if (campo.chave === 'farm') {
            return (
                <CSelect {...comum}>
                    {comOValorAtual(farms, valor).map(item => (
                        <option key={item} value={item}>{item}</option>
                    ))}
                </CSelect>
            );
        }

        if (campo.chave === 'template_banco') {
            const valores = templatesDaFarm.map(item => item.valor);
            return (
                <CSelect {...comum}>
                    <option value="">(escolha)</option>
                    {templatesDaFarm.map(item => (
                        <option key={item.farm + '|' + item.valor} value={item.valor}>
                            {item.rotulo}{item.tamanho ? ' · ' + item.tamanho : ''}
                        </option>
                    ))}
                    {valor !== '' && valores.indexOf(valor) === -1 && (
                        <option value={valor}>{valor}</option>
                    )}
                </CSelect>
            );
        }

        if (campo.chave === 'dominio') {
            return (
                <CInputGroup>
                    <CInput {...comum} autoComplete="off" />
                    <CInputGroupAppend>
                        <CButton
                            color="secondary"
                            variant="outline"
                            disabled={salvando}
                            title="Preenche slug, banco, buckets e hosts pelo padrão"
                            onClick={preencherPadrao}
                        >
                            Padrão
                        </CButton>
                    </CInputGroupAppend>
                </CInputGroup>
            );
        }

        return <CInput {...comum} autoComplete="off" />;
    };

    const renderCampo = (campo: InfraCampo) => (
        <CFormGroup>
            <CLabel htmlFor={'param-' + campo.chave}>
                {campo.rotulo}
                {!campo.editavel && (
                    <CBadge color="light" className="ml-2" title={campo.motivoTrava || ''}>
                        travado
                    </CBadge>
                )}
                {Object.prototype.hasOwnProperty.call(rascunho, campo.chave) && (
                    <CBadge color="warning" className="ml-2">alterado</CBadge>
                )}
            </CLabel>
            {renderEntrada(campo)}
        </CFormGroup>
    );

    const renderGrupo = (grupo: InfraGrupo) => {
        const doGrupo = campos.filter(campo => campo.grupo === grupo.chave);
        if (doGrupo.length === 0) {
            return null;
        }

        const linhas: Array<Array<InfraCampo>> = [];
        doGrupo.forEach(campo => {
            const indice = campo.linha - 1;
            if (!linhas[indice]) {
                linhas[indice] = [];
            }
            linhas[indice].push(campo);
        });

        const renderLinha = (daLinha: Array<InfraCampo>, chaveLinha: number) => {
            const celulas = [];
            for (let i = 0; i < daLinha.length; i++) {
                const campo = daLinha[i];
                const vizinho = daLinha[i + 1];

                if (campo.estreito && vizinho && vizinho.estreito) {
                    celulas.push(
                        <CRow key={campo.chave}>
                            <CCol xs="6">{renderCampo(campo)}</CCol>
                            <CCol xs="6">{renderCampo(vizinho)}</CCol>
                        </CRow>
                    );
                    i++;
                    continue;
                }

                celulas.push(<div key={campo.chave}>{renderCampo(campo)}</div>);
            }

            const largura = Math.max(2, Math.floor(12 / celulas.length));

            return (
                <CRow key={chaveLinha}>
                    {celulas.map((celula, indice) => (
                        <CCol md={String(largura)} key={indice}>{celula}</CCol>
                    ))}
                </CRow>
            );
        };

        return (
            <CCard className="infra-form" key={grupo.chave}>
                <CCardHeader className="infra-card-header">
                    <div>
                        <strong>{grupo.rotulo}</strong>
                        <span className="infra-card-slug">{grupo.resumo}</span>
                    </div>
                </CCardHeader>
                <CCardBody>
                    {linhas.map((daLinha, indice) => daLinha && renderLinha(daLinha, indice))}
                </CCardBody>
            </CCard>
        );
    };

    if (carregando && campos.length === 0) {
        return <div className="infra-carregando"><CSpinner color="primary" /></div>;
    }

    return (
        <div className="infra-parametros">
            {erro && <CAlert color="danger">{erro}</CAlert>}
            {aviso && <CAlert color="warning">{aviso}</CAlert>}

            {grupos.map(renderGrupo)}

            <div className="infra-form-acoes">
                {criando ? (
                    <CButton color="primary" onClick={criar}
                        disabled={salvando || valorDe('nome') === '' || valorDe('dominio') === ''}>
                        {salvando ? 'Criando…' : 'Criar pipeline'}
                    </CButton>
                ) : (
                    <CButton color="primary" onClick={salvar} disabled={alterados.length === 0 || salvando}>
                        {salvando ? 'Salvando…' : 'Salvar parâmetros'}
                    </CButton>
                )}
                {alterados.length > 0 && !criando && (
                    <CButton color="secondary" variant="outline" disabled={salvando}
                        onClick={() => setRascunho({})}>
                        Descartar
                    </CButton>
                )}
                {!criando && (
                    <span className="infra-card-slug">
                        {alterados.length > 0
                            ? alterados.length + ' alterado' + (alterados.length > 1 ? 's' : '')
                            : 'nada alterado'}
                    </span>
                )}
            </div>
        </div>
    );
};

export default Parametros;
